import { Component, ElementRef, HostListener, inject, OnInit, viewChild, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ApplicationType, IBaseCard, IFirmTaxprepT1Card, JobsBillingCard, TaxprepT1Card } from "@app/views-interfaces";
import { firstValueFrom, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { AnalyticsService, DashboardsService, FiltersService, MappingOfStatusesService, SearchCardsResponse } from "../shared";
import { SentryService } from "../shared/analytics-service/sentry.service";
import { CardDisplayService } from "../shared/card-display/card-display.service";
import { CardService } from "../shared/card-service/card.service";
import { HelpersService } from "../shared/helpers-service/helpers.service";
import { CardEventType } from "../shared/interfaces/card-event-type.enum";
import { IDashboardModel, isJobsBillingDashboard } from "../views-interfaces/dashboard-model.interface";
import { SearchResultComponent } from "./search-result-component/search-result.component";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "wk-search",
    templateUrl: "./search.component.html",
    styleUrl: "./search.component.scss",
    host: { class: "wk-search" },
    imports: [FormsModule, ReactiveFormsModule, SearchResultComponent, TranslatePipe],
})
export class SearchComponent implements OnInit {
    private readonly cardsService = inject(CardService);
    private readonly sentryService = inject(SentryService);
    private readonly helpersService = inject(HelpersService);
    private readonly filtersService = inject(FiltersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly cardDisplayService = inject(CardDisplayService);

    static readonly taxSearchTerms = [
        "cardId",
        "firstName",
        "lastName",
        "familyMembers",
        "familyMembersR",
        "name",
        "phone",
        "cellPhone",
        "email",
        "groupName",
        "fileName",
        "clientCodeId",
        "clientCodePin",
        "clientCodeIFirm",
    ];
    static readonly minimaliFirmTaxSearchTerms = ["clientCodeIFirm"];
    static readonly minimalTaxSearchTerms = ["clientCodeId", "clientCodePin", "clientCodeIFirm"];
    static readonly jobsSearchTerms = ["cardId", "jobId", "firstName", "lastName", "name", "phone", "cellPhone", "email", "clientCode"];
    static readonly minimalJobsSearchTerms = ["jobId", "clientCode"];

    protected cards: IBaseCard[] = [];
    protected term = new FormControl("");
    protected inputFocused = false;
    protected showLoader = false;
    private searchRequest: Subscription | undefined = undefined;
    private isSearchResultOpen = false;
    private currentDashboard: IDashboardModel | undefined = undefined;
    private readonly inputElement = viewChild<ElementRef>("searchInput");
    protected minimumCharactersForSearch: 1 | 3 = 3;
    protected searchTerm = signal<string>("");

    constructor() {
        this.cardDisplayService.cardChangedEvent$.pipe(takeUntilDestroyed()).subscribe((event) => {
            if (event.eventType === CardEventType.cardDeleted) {
                this.handleCardDeleted(event.oldCard);
            }
        });
        this.dashboardsService.dashboardChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.term.reset("");
            this.cards = [];
        });
    }

    handleCardDeleted(data: IBaseCard) {
        const index = this.cards.findIndex((card) => card.cardId === data.cardId);
        if (index > -1) {
            this.cards.splice(index, 1);
        }
    }

    handleSearchResult(data: SearchCardsResponse) {
        this.showLoader = false;
        if (data.Code === 200 && data.Cards && data.Cards.length > 0) {
            this.analyticsService.track("OnePageApp: Search found results");

            this.cards = data.Cards.filter((card) => this.filtersService.isCardAvailableForSearch(card));
            this.cards.filter((card) => !this.cardDisplayService.getCardById(card.cardId)).forEach((card) => this.cardDisplayService.storeCard(card));

            if (isJobsBillingDashboard(this.currentDashboard)) {
                (this.cards as JobsBillingCard[]).forEach((card) => {
                    const step = this.mappingOfStatusesService.getStepFromStatusId(card.job.statusId);
                    // TODO : there must be an other way of handling card status IDs in display than stapling it to the card
                    card.statusIds =
                        step?.Statuses?.map((x) => {
                            return x.TaxReturnStatusId;
                        }) ?? [];
                });
            } else {
                (this.cards as TaxprepT1Card[] | IFirmTaxprepT1Card[]).forEach((card) => {
                    card.statusIds = [];
                    const step = this.mappingOfStatusesService.getStepFromStatusId(card.return.statusId);
                    if (step) {
                        // TODO : there must be an other way of handling card status IDs in display than stapling it to the card
                        card.statusIds = step.Statuses.map((x) => {
                            return x.TaxReturnStatusId;
                        });
                        const statuses = {
                            statuses: step.Statuses.map((x) => {
                                return {
                                    TaxReturnStatus: {
                                        Id: x.TaxReturnStatusId,
                                    },
                                    Role: {
                                        Id: x.RoleId,
                                    },
                                };
                            }),
                        };
                        // TODO : could we just compute that in the small card?
                        card.assignedUser = this.helpersService.getAssignedUserForCardT1(card, statuses);
                    }
                });
            }
        } else {
            this.cards = [];
        }
    }

    onBlur() {
        setTimeout(() => {
            this.inputFocused = false;
            this.showLoader = false;
            this.cards = [];
            this.searchTerm.set("");
        }, 300);
    }

    ngOnInit() {
        this.init();
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.minimumCharactersForSearch = isJobsBillingDashboard(this.currentDashboard) ? 1 : 3;

        this.term.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((term) => {
            const sanitizedInput = this.sanitizeInput(term ?? "");
            const minimumSearchLengthReached = sanitizedInput.length >= this.minimumCharactersForSearch;
            if (sanitizedInput !== "" && minimumSearchLengthReached) {
                this.showLoader = true;
            } else {
                this.cards = [];
                return false;
            }

            if (minimumSearchLengthReached) {
                this.analyticsService.track("OnePageApp : Doing a research");
            }

            if (this.searchRequest) {
                this.searchRequest.unsubscribe();
            }

            if (minimumSearchLengthReached) {
                this.sentryService.trace("WebApp_Performance-SearchCards", "CardsSearch", async () => {
                    const data = await firstValueFrom(this.searchCards(sanitizedInput));
                    this.handleSearchResult(data);
                    return [
                        {
                            key: "Total cards found",
                            value: data?.Cards?.length ?? 0,
                        },
                    ];
                });
            }
        });
    }

    onFocus() {
        const sanitizedInput = this.sanitizeInput(this.term.value ?? "");
        if (sanitizedInput !== "" && sanitizedInput.length >= this.minimumCharactersForSearch && !this.isSearchResultOpen) {
            this.searchRequest = this.searchCards(sanitizedInput).subscribe((data) => this.handleSearchResult(data));
        } else if (this.isSearchResultOpen && sanitizedInput !== "" && sanitizedInput.length >= this.minimumCharactersForSearch) {
            this.inputFocused = true;
        }
    }

    searchCards(term: string) {
        this.isSearchResultOpen = true;
        this.inputFocused = true;
        this.showLoader = true;
        this.cards = [];
        this.searchTerm.set(term);
        const filterString = this.getFilterString(term, this.currentDashboard?.ApplicationType);
        return this.cardsService.searchCardsByTerm(filterString);
    }

    sanitizeInput(input: string) {
        return input
            .trim()
            .replaceAll("'", "''")
            .toLowerCase()
            .normalize("NFD")
            .replaceAll(/[\u0300-\u036f]/g, "");
    }

    /**
     * Generate the odata filter query to append to the http request. Tax and J&B have
     * different fields to query, and for J&B we can also search for exact match on job
     * ID and client code for 1 or 2 characters.
     */
    getFilterString(term: string, type: ApplicationType | undefined): string {
        let searchableTerms = SearchComponent.taxSearchTerms;
        const individualSearchQueryFunction = term.length <= 2 ? this.exactMatch : this.partialMatch;

        if (term.length <= 2) {
            if (type === ApplicationType.JobsBilling) {
                searchableTerms = SearchComponent.minimalJobsSearchTerms;
            } else if (type === ApplicationType.iFirmTaxT1) {
                searchableTerms = SearchComponent.minimaliFirmTaxSearchTerms;
            } else if (type === ApplicationType.TaxprepT1) {
                searchableTerms = SearchComponent.minimalTaxSearchTerms;
            }
        } else {
            if (type === ApplicationType.JobsBilling) {
                searchableTerms = SearchComponent.jobsSearchTerms;
            } else if (type === ApplicationType.iFirmTaxT1 || type === ApplicationType.TaxprepT1) {
                searchableTerms = SearchComponent.taxSearchTerms;
            }
        }

        return searchableTerms
            .map((searchable) => {
                return { name: searchable, term: this.normalize(searchable, term) };
            })
            .filter((item) => item.term?.length > 0)
            .map(individualSearchQueryFunction)
            .join("%20or%20");
    }

    partialMatch(item: { name: string; term: string }) {
        return "contains(searchableTerms/" + item.name + ",%20%27" + item.term + "%27)%20eq%20true";
    }

    exactMatch(item: { name: string; term: string }) {
        return "searchableTerms/" + item.name + "%20eq%20%27" + item.term + "%27";
    }

    normalize(searchTerm: string, input: string): string {
        switch (searchTerm) {
            case "jobId":
            case "phone":
            case "cellPhone": {
                if (/[a-zA-Z]/.test(input)) {
                    // Has a letter, don't attempt to search phone
                    return "";
                }

                const phone = input.match(/\d+/g);
                if (!phone || phone.length === 0) {
                    return "";
                }
                return phone.join("");
            }
            default:
                return input;
        }
    }

    @HostListener("document:keydown.escape")
    onKeydownHandler() {
        this.isSearchResultOpen = true;
        this.inputFocused = false;
        this.showLoader = false;
    }

    @HostListener("document:click", ["$event"])
    documentClick(event: MouseEvent) {
        const element = event.target as HTMLElement;
        if (element !== this.inputElement()?.nativeElement) {
            this.isSearchResultOpen = true;
            this.inputFocused = false;
            this.showLoader = false;
        }
    }
}
